/**
 * Helpers for showing a markdown response while it is still being generated.
 *
 * A prefix of a markdown document often renders differently from how the finished document will: an unclosed
 * `**` shows its asterisks, a half-written link shows its brackets, a table's header row shows as a paragraph
 * until the separator row arrives, and a lone `-` under a line turns that line into a heading. Each of those
 * flips once the next few characters arrive, so the text visibly jumps. `prepareStreamingMarkdown` closes or
 * holds back these pieces, so what's shown is always laid out the way it will be once complete.
 */

const FENCE_PATTERN = /^ {0,3}(`{3,}|~{3,})\s*([^\s`]*)/;
const PARTIAL_FENCE_PATTERN = /^ {0,3}(`{1,}|~{1,})\s*$/;

// rendered as diagrams, which can't be drawn from part of their source
const HELD_LANGUAGES = new Set(['mermaid', 'svg', 'svg+xml']);

// list, heading, quote and rule markers with nothing after them yet; a `-` or `=` line also turns the line above
// it into a heading
const MARKUP_ONLY_LINE = /^\s*(?:[-*+]|\d+[.)]|#{1,6}|>|[-=_*]+)?\s*$/;

const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)*\|?\s*$/;

// a link from its `[` to the end of the text that hasn't been closed yet
const INCOMPLETE_LINK = /\[[^\]\n]*(?:\](?:\([^)\n]*)?)?$/;

const isTableLine = (line) => line.trim().startsWith('|');

const countOf = (text, pattern) => (text.match(pattern) || []).length;

function findOpenFence(lines) {
  let openFence = null;

  lines.forEach((line, index) => {
    const match = FENCE_PATTERN.exec(line);
    if (!match) {
      return;
    }
    const [, marker, info] = match;
    if (!openFence) {
      openFence = { index, marker, language: info.toLowerCase() };
    } else if (marker[0] === openFence.marker[0] && marker.length >= openFence.marker.length && !info) {
      openFence = null;
    }
  });

  return openFence;
}

/**
 * Prepares the part of a markdown response received so far for rendering.
 *
 * @returns {{ markdown: string, pending: null | 'table' | 'diagram' }} the markdown to render, and what is being
 *   held back while it is incomplete, so a placeholder can stand in for it
 */
export function prepareStreamingMarkdown(text) {
  const lines = text.split('\n');
  // the line still being written; '' once the text ends with a newline
  const lastIndex = lines.length - 1;
  let openFence = findOpenFence(lines);

  // an opening fence still being written may not have its language yet
  if (openFence && openFence.index === lastIndex) {
    lines[lastIndex] = '';
    openFence = null;
  }

  if (openFence) {
    if (HELD_LANGUAGES.has(openFence.language)) {
      return { markdown: [...lines.slice(0, openFence.index), ''].join('\n'), pending: 'diagram' };
    }

    // a code block can show as it grows; close it for now, without any part of its real closing fence
    if (PARTIAL_FENCE_PATTERN.test(lines[lastIndex])) {
      lines.pop();
    }
    const body = lines.join('\n');
    return { markdown: `${body}${body.endsWith('\n') ? '' : '\n'}${openFence.marker}`, pending: null };
  }

  // a table shows once its header and separator are complete, then one complete row at a time
  if (isTableLine(lines[lastIndex])) {
    lines[lastIndex] = '';
  }
  let tableStart = lastIndex;
  while (tableStart > 0 && isTableLine(lines[tableStart - 1])) {
    tableStart -= 1;
  }
  if (tableStart < lastIndex) {
    const separatorIndex = tableStart + 1;
    if (separatorIndex >= lastIndex || !TABLE_SEPARATOR.test(lines[separatorIndex])) {
      return { markdown: [...lines.slice(0, tableStart), ''].join('\n'), pending: 'table' };
    }
    return { markdown: lines.join('\n'), pending: null };
  }

  let last = lines[lastIndex].replace(INCOMPLETE_LINK, '');

  // emphasis can span the lines of a paragraph, so balance it across the whole paragraph
  let paragraphStart = lastIndex;
  while (paragraphStart > 0 && lines[paragraphStart - 1].trim() !== '') {
    paragraphStart -= 1;
  }
  const paragraphOf = (lastLine) => [...lines.slice(paragraphStart, lastIndex), lastLine].join('\n');

  if (countOf(paragraphOf(last), /`/g) % 2 === 1) {
    last = last.endsWith('`') ? last.slice(0, -1) : `${last}\``;
  }

  const outsideCode = paragraphOf(last).replace(/`[^`]*`/g, '');
  if (countOf(outsideCode, /\*\*/g) % 2 === 1) {
    const trimmed = last.trimEnd();
    last = trimmed.endsWith('**') ? trimmed.slice(0, -2) : `${trimmed}**`;
  }

  if (MARKUP_ONLY_LINE.test(last)) {
    last = '';
  }

  lines[lastIndex] = last;
  return { markdown: lines.join('\n'), pending: null };
}

// the slowest the text is revealed, so a slow stream still reads as a steady flow
const MIN_CHARS_PER_SECOND = 60;
// how quickly a backlog is worked off: about two thirds of it in this time
const CATCH_UP_MS = 220;

const isWhitespace = (char) => /\s/.test(char);

/**
 * How much of `text` to show after `elapsedMs` more of revealing it, having shown `revealed` characters.
 *
 * Text arrives in bursts as the network delivers it; revealing it at a pace that follows the backlog smooths
 * those bursts into a steady flow, without ever falling far behind. It reveals whole words, so a word never
 * grows at the end of a line and then jumps to the next one, and while streaming it holds back the last word,
 * which may not be complete yet.
 */
export function nextRevealLength({ text, revealed, elapsedMs, isStreaming }) {
  const shown = Math.min(revealed, text.length);
  const backlog = text.length - shown;
  if (backlog <= 0) {
    return text.length;
  }

  const step = Math.max((MIN_CHARS_PER_SECOND * elapsedMs) / 1000, backlog * (1 - Math.exp(-elapsedMs / CATCH_UP_MS)));
  let target = Math.min(text.length, Math.ceil(shown + step));

  // finish the word the target lands in
  while (target < text.length && !isWhitespace(text[target])) {
    target += 1;
  }

  if (target === text.length && isStreaming && !isWhitespace(text[text.length - 1])) {
    // the last word may still be growing: stop before it
    let wordStart = target;
    while (wordStart > shown && !isWhitespace(text[wordStart - 1])) {
      wordStart -= 1;
    }
    return Math.max(shown, wordStart);
  }

  return target;
}
